import * as types from 'store/mutation-types'
import {
  fetchStockCorActionList,
  fetchWatchList,
  reqAddWatchList,
  reqRemoveWatchList,
  reqUpdateWatchList
} from 'apis/stock'
import { MARKET_TYPE, SEC_TYPE } from 'const/stock'
import Stock from 'utils/stock'
import Option from 'utils/option'
import getLocalFixedQuoteDetail from 'utils/getLocalFixedQuoteDetail'
import getOptionExpiryStamp from 'utils/getOptionExpiryStamp'
import isSameStock from 'utils/isSameStock'

/**
 * 修复自选列表股权类型的数据结构
 * @param {object} stock
 */
function fixWatchListFut(stock) {
  if (stock.futContent && stock.futContent.securityType === SEC_TYPE.FUT) {
    const multiOffset =
      stock.futContent.productPriceConfig.displayMultiplier.offset
    const multiVal = stock.futContent.productPriceConfig.displayMultiplier.value
    const multiplier = multiVal * Math.pow(10, `-${multiOffset}`)
    const times = Math.pow(10, `-${stock.futContent.priceOffset}`) * multiplier
    stock.latestPrice = stock.latestPrice * times
    stock.preClose = stock.preClose * times
    stock.change = stock.latestPrice - stock.preClose
    if (!stock.preClose) stock.changeRate = 0
    else stock.changeRate = stock.change / stock.preClose
  }
  return stock
}

let initRequest = null
const actions = {
  addToWatchlist({ dispatch }, payload) {
    let group = 0
    let items
    if (typeof payload === 'string') {
      items = [{ symbol: payload, group: 0 }]
    } else {
      items = payload
      group = payload[0].group
    }
    return reqAddWatchList(items).then(resp => {
      if (resp.data.ret === 0) {
        dispatch('initWatchlist', group)
      } else {
        dispatch('addWatchlistNotify', resp.data.msgCn)
      }
    })
  },
  deleteFromWatchlist({ commit, dispatch }, watchlistPayload) {
    let items
    if (typeof watchlistPayload === 'string') {
      items = [{ symbol: watchlistPayload }]
    } else {
      items = watchlistPayload
    }
    reqRemoveWatchList(items).then(() => {
      dispatch('loadGroupList') // For GroupStocks deletion
      commit(types.DELETE_FROM_WATCHLIST, items)
    })
  },
  getUpdatedOptionBriefs({ getters }, stocks) {
    if (!getters.hasUsOptionPermission) return Promise.resolve(stocks)
    const opts = stocks
      .filter(t => t.secType === SEC_TYPE.OPT)
      .map(t => ({
        symbol: t.symbol,
        expiry: getOptionExpiryStamp(t.expiry),
        strike: t.strike,
        right: t.right
      }))
    if (opts.length === 0) return Promise.resolve(stocks)
    return Option.getAllOptionDetails(opts).then(options => {
      return stocks.map(stock => {
        const target = options.find(o => isSameStock(stock, o))
        return {
          ...stock,
          ...target
        }
      })
    })
  },
  getUpdatedStockBriefs({ getters }, stocks) {
    const stks = stocks.filter(
      stock =>
        [MARKET_TYPE.US, MARKET_TYPE.HK].includes(stock.market) &&
        stock.secType !== SEC_TYPE.OPT
    )
    if (stks.length === 0) return Promise.resolve(stocks)
    const symbols = stks.map(item => ({ symbol: item.symbol }))
    return Stock.getAllStockBriefs({ items: symbols }).then(items => {
      return stocks.map(stock => {
        const target = items.find(t => isSameStock(t, stock))
        return {
          ...stock,
          ...target
        }
      })
    })
  },
  getUpdatedStockCorActions({ getters }, stocks) {
    const symbols = stocks
      .filter(t => t.secType !== SEC_TYPE.OPT)
      .map(t => t.symbol)
    return fetchStockCorActionList(symbols)
      .then(corporates => {
        return stocks.map(stock => {
          const target = corporates.find(c => c.symbol === stock.symbol)
          return {
            ...stock,
            corporates: (stock.corporates || []).push(target)
          }
        })
      })
      .catch(() => {
        return stocks
      })
  },
  // 初始化持仓分组
  initWatchlist({ commit, getters, dispatch }) {
    const params = {
      manualRefresh: true,
      group: getters.curWatchGroup,
      hkDelay: getters.hkDelay
    }
    return fetchWatchList(params, {
      before(req) {
        if (initRequest) {
          initRequest.abort()
        }
        initRequest = req
      }
    })
      .then(({ data: respData }) => {
        initRequest = null
        const items = getLocalFixedQuoteDetail(respData.items || [])
        return dispatch('getUpdatedOptionBriefs', items)
      })
      .then(stocks => {
        stocks = (stocks || []).map(fixWatchListFut)
        return dispatch('getUpdatedStockCorActions', stocks)
      })
      .then(stocks => {
        commit(types.INIT_WATCHLIST, { stocks })
        return stocks
      })
  },
  refreshWatchlist({ commit, getters, dispatch }, refreshList) {
    const stocks = refreshList
      .filter(stock => stock.secType !== SEC_TYPE.OPT)
      .map(stock => ({ symbol: stock.symbol }))
    const opts = refreshList.filter(opt => opt.secType === SEC_TYPE.OPT)
    if (stocks.length > 0) {
      Stock.getAllStockBriefs({
        lite: true,
        hkDelay: getters.hkDelay,
        items: stocks
      }).then(lists => commit(types.UPDATE_WATCHLIST, lists))
    }
    if (opts.length > 0) {
      dispatch('getUpdatedOptionBriefs', opts).then(lists => {
        commit(types.UPDATE_WATCHLIST, lists)
      })
    }
  },
  saveNewWatchlistOrder({ commit, getters, dispatch }, list) {
    commit(types.UPDATE_WATCHLIST_DISPLAY, list)
    const group = getters.curWatchGroup
    const newList = list.map(val => {
      const symbol = val.webLocal ? val.webLocal.symbol : val.symbol
      return {
        group,
        symbol
      }
    })
    if (newList.length > 0) {
      // If an empty array is sent to API, all group lists clear
      reqUpdateWatchList(newList).then(() => {
        dispatch('initWatchlist', group)
      })
    }
  },
  addWatchlistNotify({ commit }, message) {
    commit(types.ADD_WATCHLIST_NOTIFY, message)
  },
  removeWatchlistNotify({ commit }) {
    commit(types.REMOVE_WATCHLIST_NOTIFY)
  },
  updateWatchlistGroup({ commit }, group) {
    commit(types.UPDATE_WATCHLIST_GROUP, group)
  },
  updateWatchlistMarket({ commit }, market) {
    commit(types.UPDATE_WATCHLIST_MARKET, market)
  }
}

export default { actions }
